import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Container, Dropdown, Navbar, Offcanvas } from 'react-bootstrap';
import cx from 'classnames';
import CurrentPageContext from '../../../contexts/CurrentPageContext';
import style from './Navigation.scss';
import ExternalLink from './ExternalLink';

const propTypes = {
  navigation: PropTypes.arrayOf(PropTypes.shape({
    page: PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.string.isRequired,
    }),
    text: PropTypes.string,
    link: PropTypes.shape({
      target: PropTypes.string,
      href: PropTypes.string,
    }),
    navigationItems: PropTypes.array,
  })),
  onSelect: PropTypes.func,
};

const defaultProps = {
  navigation: [],
  onSelect: undefined,
};

class Navigation extends React.Component {
  static contextType = CurrentPageContext;

  constructor(props) {
    super(props);

    this.dropdownRefs = {};

    this.state = {
      openNavs: {},
      navExpanded: false,
    };
  }

  setDropdownRef = (id, element) => {
    if (element) {
      // If the element is not null, store it in dropdownRefs
      this.dropdownRefs[id] = element;
    }
  };

  openDropdown = (id) => {
    this.setState(({ openNavs }) => ({
      openNavs: { ...openNavs, [id]: true },
    }), () => {
      const isMobile = /Mobi/.test(navigator.userAgent);
      if (!isMobile) {
        let max = 0;
        const dropdownRef = this.dropdownRefs[id];

        if (!dropdownRef || !dropdownRef.querySelector) {
          return;
        }

        const menu = dropdownRef.querySelector('.dropdown-menu');
        if (!menu) {
          return;
        }

        const pos = menu.getBoundingClientRect();
        let currentWidth = pos.width;

        // Calculate maximum width of child elements
        Object.keys(menu.children).forEach((idx) => {
          const { width } = menu.children[idx].getBoundingClientRect();
          if (width > max) { max = width; }
        });

        if (max > pos.width) {
          currentWidth = max;
          menu.style.width = `${max}px`;
        }

        const right = pos.x + currentWidth;
        if (window.innerWidth < right) {
          menu.style.left = 0;
          menu.style.top = '100%';
          menu.style.marginTop = 0;
        }
      }
    });
  };

  resetExpanded = () => {
    document.body.classList.remove('hide-overflow');
    this.setState({ navExpanded: false });
  }

  toggleExpanded = () => {
    const { navExpanded } = this.state;
    document.body.classList.toggle('hide-overflow', !navExpanded);
    this.setState({ navExpanded: !navExpanded });
  }

  closeDropdown = (id) => {
    this.setState(({ openNavs }) => ({
      openNavs: { ...openNavs, [id]: false },
    }));
  }

  renderWithoutChildren(item, index, topLevel) {
    const title = item.text || (item.page && item.page.title);
    if (!title) { return false; }
    const { link } = item;

    const { primaryColorBorderHoverClass } = this.context;

    const target = (link && link.target) || '_blank';

    const { onSelect } = this.props;

    const sharedClasses = cx(
      primaryColorBorderHoverClass,
      style.singleLink,
      style.height,
      { [style.topLevel]: topLevel },
    );


    if (link) {
      const { href } = link;
      return (
        <ExternalLink
          href={href}
          target={target}
          className={cx(sharedClasses, style.externalLink)}
          child={title}
          key={index}
        />
      );
    }

    const path = (link && link.href) || (item.page && item.page.path);
    if (path === undefined || path === null) { return false; }

    return (
      <div
        key={`nav-link-${index}`}
        className={sharedClasses}
      >
        <a href={path} onClick={onSelect}>
          {title}
        </a>
      </div>
    );
  }

  renderWithChildren(item, index, topLevel) {
    const { openNavs } = this.state;

    const { primaryColorBorderHoverClass } = this.context;

    const { navigationItems } = item;
    let children;
    if (navigationItems && navigationItems.length) {
      children = navigationItems.map((child, cIndex) => this.renderItem(child, `${index}${cIndex}`, null));
    }

    const name = item.text || (item.page && item.page.title);
    const path = (item.link && item.link.href) || (item.page && item.page.path);

    let link;
    if (path !== undefined && path !== null) {
      link = (
        <a href={path} onClick={this.onSelect}>
          {name}
        </a>
      );
    }

    const unique = `nav-dropdown-${index}-${name}`;

    return (
      <Dropdown
        id={unique}
        key={unique}
        className={cx(primaryColorBorderHoverClass, topLevel ? style.topLevel : style.parent)}
        onMouseEnter={() => this.openDropdown(unique)}
        onMouseLeave={() => this.closeDropdown(unique)}
        open={openNavs[unique]}
        onToggle={open => (open ? this.openDropdown(unique) : this.closeDropdown(unique))}
        ref={element => this.setDropdownRef(unique, element)}
      >
        <Dropdown.Toggle
          id={`nav-dropdown-toggle-${index}`}
          className={cx(style.darkerGrey, style.height)}
        >
          {link || name}
        </Dropdown.Toggle>
        <Dropdown.Menu
          show={openNavs[unique]}
        >
          {children}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  renderItem = (item, index, topLevel = false) => {
    let component;

    if (item.navigationItems && item.navigationItems.length > 0) {
      component = this.renderWithChildren(item, index, topLevel);
    } else {
      component = this.renderWithoutChildren(item, index, topLevel);
    }

    return component;
  }

  render() {
    const { navigation } = this.props;
    const { navExpanded } = this.state;
    const className = cx(
      style.root,
      navExpanded && style.expanded,
    );

    const navItems = navigation.map((nav, idx) => this.renderItem(nav, idx, true));
    return (
      <Navbar
        className={className}
        onToggle={this.toggleExpanded}
        expanded={navExpanded}
      >
        <Container className={style.headerNav}>
          <div className={style.fullWidth}>
            <Offcanvas.Header>
              <Navbar.Toggle onClick={this.toggleExpanded} />
            </Offcanvas.Header>
          </div>
          <div className={style.fullWidth}>
            <Navbar.Collapse className={cx(style.collapse, style.navContainer)}>
              <div className={style.navWrapper}>
                <div className={style.navButtonWrapper}>
                  {navItems}
                </div>
              </div>
            </Navbar.Collapse>
          </div>
        </Container>
      </Navbar>
    );
  }
}

Navigation.propTypes = propTypes;
Navigation.defaultProps = defaultProps;

Navigation.fragments = (() => {
  const page = gql`
    fragment NavigationItemPageFields on Page {
      path
      title
    }
  `;

  const navigationItemFields = gql`
    fragment NavigationItemFields on NavigationItem {
      id
      text
      link {
        href
        target
      }
      page {
        id
        ...NavigationItemPageFields
      }
    }

    ${page}
  `;

  return {
    navigationItem: gql`
      fragment NavigationItems on NavigationItem {
        ...NavigationItemFields
        navigationItems {
          ...NavigationItemFields
          navigationItems {
            ...NavigationItemFields
            navigationItems {
              ...NavigationItemFields
            }
          }
        }
      }

      ${navigationItemFields}
    `,
    page,
  };
})();

export default Navigation;
